import { Locales, Nullable } from "@simplyk/common";
import { getTranslationLabelLanguage } from "../helpers/loadTranslationLabels";
import { FieldError, FieldValues, Path, RegisterOptions, UseFormSetValue } from "react-hook-form";

import { useTranslateField } from "./useTranslateField";
import { TranslationEditorField } from "./TranslationEditorField";

import { useTranslate } from "@/common/hooks/useTranslate";
import { DashboardSkeleton } from "@/dashboard-design-system/Skeleton/DashboardSkeleton";
import { OutlinedMailFieldWysiwyg } from "@/components/OutlinedMailFieldWysiwyg/OutlinedMailFieldWysiwyg";
import { OutlinedTextFieldWysiwyg } from "@/components/OutlinedTextFieldWysiwyg/OutlinedTextFieldWysiwyg";
import { CustomFroalaConfig, FroalaConfig } from "@/components/OutlinedTextFieldWysiwyg/type";

interface TranslatedFieldWysiwygProps<T extends FieldValues> {
  fieldName: Path<T>;
  defaultFieldValue: string | undefined | null;
  defaultTranslationFieldValue: string | undefined | null;
  setValue: UseFormSetValue<T>;
  translationLocale: Locales;
  error: FieldError | undefined;
  config: FroalaConfig;
  dataTest: string;
  customConfig?: CustomFroalaConfig | undefined;
  subjectRef: React.MutableRefObject<Nullable<HTMLInputElement>>;
  attachmentKeyName?: Path<T>;
  rules?: RegisterOptions<FieldValues, string>[] | undefined;
  skipTranslation?: boolean;
}

export function TranslatedFieldWysiwyg<T extends FieldValues>({
  fieldName,
  defaultFieldValue,
  defaultTranslationFieldValue,
  setValue,
  translationLocale,
  config,
  dataTest,
  error,
  customConfig,
  subjectRef,
  rules,
  attachmentKeyName,
  skipTranslation = false,
}: TranslatedFieldWysiwygProps<T>) {
  const { t } = useTranslate();
  const { isTranslating } = useTranslateField({
    fieldName,
    defaultFieldValue,
    defaultTranslationFieldValue,
    setValue,
    translationLocale,
    skipTranslation,
  });

  const label = t(
    "dashboard",
    fieldName.endsWith(".description") ? "translation.campaignDescription" : "translation.emailContent",
    {
      lng: getTranslationLabelLanguage(translationLocale),
    }
  );

  return (
    <TranslationEditorField label={label} name={fieldName}>
      {isTranslating ? (
        <DashboardSkeleton variant="rounded" width="100%" sx={(theme) => ({ height: theme.spacing(40) })} />
      ) : config === FroalaConfig.Minimalist ? (
        <OutlinedTextFieldWysiwyg
          id={fieldName}
          name={fieldName}
          defaultValue={defaultTranslationFieldValue || ""}
          keyName={fieldName}
          heightMinOver310={400}
          compact
          error={error}
          config={config}
          data-test={dataTest}
        />
      ) : (
        <OutlinedMailFieldWysiwyg
          name={fieldName}
          subjectName={fieldName}
          subjectRef={subjectRef}
          defaultValue={defaultTranslationFieldValue || ""}
          attachmentKeyName={attachmentKeyName}
          rules={rules}
          heightMinOver310={400}
          compact
          compactEmailActions={fieldName.endsWith(".emailContent") ? "menu" : "toolbar"}
          height={fieldName.endsWith(".emailContent") ? 197 : undefined}
          error={error}
          data-test={dataTest}
          customConfig={customConfig}
          config={config}
        />
      )}
    </TranslationEditorField>
  );
}
